import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, resolveFileUrl } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  ArrowLeft, BadgeCheck, Wallet, Users as UsersIcon, KeyRound, Heart, Award,
  Calendar as CalendarIcon, Mail, Phone, MapPin, Upload, Trash2, Pencil, Plus,
  AlertCircle, ImageOff, GraduationCap, ShieldCheck, Copy, BookOpen, DollarSign,
} from "lucide-react";
import { toast } from "sonner";

const PHOTO_MAX_KB = 150;
const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export default function StudentProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  // Excel-imported students may store gender in Arabic ("ذكر"/"أنثى") instead of male/female
  const genderLabel = (g) => (g === "male" ? t("gender_male") : g === "female" ? t("gender_female") : g);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});

  const [credOpen, setCredOpen] = useState(false);
  const [creds, setCreds] = useState(null);

  const [guardOpen, setGuardOpen] = useState(false);
  const [guardForm, setGuardForm] = useState({ name: "", relation: "", phone: "", email: "", is_primary: false, can_pickup: true });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/students/${id}/profile`);
      setProfile(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const openEdit = () => {
    setForm({
      ...profile,
      guardians: profile.guardians || [],
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    try {
      const p = { ...form };
      // strip enriched fields
      ["class_detail", "grade_detail", "parent_account", "student_account", "wallet",
        "attendance", "behavior", "fees_due", "class_homework_count",
        "created_at", "updated_at"].forEach((k) => delete p[k]);
      await api.put(`/students/${id}`, p);
      toast.success(t("updated_successfully"));
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("photo_must_be_image"));
      return;
    }
    if (file.size > PHOTO_MAX_KB * 1024) {
      toast.error(t("photo_too_large", { kb: Math.ceil(file.size / 1024), max: PHOTO_MAX_KB }));
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/students/${id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("photo_uploaded"));
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && (d.message || d)) || t("error"));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    if (!profile.photo_url) return;
    if (!window.confirm(t("delete_photo_confirm"))) return;
    try {
      await api.delete(`/students/${id}/photo`);
      toast.success(t("deleted_successfully"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const createParentAccount = async () => {
    try {
      const r = await api.post(`/students/${id}/create-parent-account`);
      setCreds(r.data);
      setCredOpen(true);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((d && (d.message || d)) || t("error"));
    }
  };

  const regenLoginCode = async () => {
    try {
      const r = await api.post(`/students/${id}/login-code`);
      toast.success(`${t("student_login_code")}: ${r.data.login_code}`, { duration: 12000 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    }
  };

  const addGuardian = async () => {
    if (!guardForm.name.trim()) {
      toast.error(t("guardian_name_required"));
      return;
    }
    const next = [...(profile.guardians || []), { ...guardForm }];
    await api.put(`/students/${id}`, { ...profile, guardians: next,
      class_detail: undefined, grade_detail: undefined, parent_account: undefined,
      student_account: undefined, wallet: undefined, attendance: undefined,
      behavior: undefined, fees_due: undefined, class_homework_count: undefined });
    setGuardForm({ name: "", relation: "", phone: "", email: "", is_primary: false, can_pickup: true });
    setGuardOpen(false);
    // Task 3: if the guardian has an email, also create a parent login (set-password link).
    if (guardForm.email && guardForm.email.includes("@")) {
      try {
        const r = await api.post(`/students/${id}/guardians/create-account`, {
          email: guardForm.email, name: guardForm.name, phone: guardForm.phone,
        });
        toast.success(r.data.reset_email_sent
          ? `✉️ تم إنشاء حساب ولي الأمر وإرسال رابط تعيين كلمة المرور إلى ${r.data.email}`
          : `تم إنشاء حساب ولي الأمر (${r.data.email})`, { duration: 10000 });
      } catch (err) {
        toast.error(err?.response?.data?.detail || t("added_successfully"));
      }
    } else {
      toast.success(t("added_successfully"));
    }
    load();
  };

  const removeGuardian = async (idx) => {
    if (!window.confirm(t("delete_guardian_confirm"))) return;
    const next = (profile.guardians || []).filter((_, i) => i !== idx);
    await api.put(`/students/${id}`, { ...profile, guardians: next,
      class_detail: undefined, grade_detail: undefined, parent_account: undefined,
      student_account: undefined, wallet: undefined, attendance: undefined,
      behavior: undefined, fees_due: undefined, class_homework_count: undefined });
    toast.success(t("deleted_successfully"));
    load();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t("copied_to_clipboard"));
  };

  if (loading || !profile) {
    return <Card className="p-12 text-center text-muted-foreground">{t("loading")}…</Card>;
  }

  const initials = (profile.full_name_ar || profile.full_name || "")
    .split(" ").slice(0, 2).map((p) => p[0]).join("");
  const attBadge = (() => {
    const p = profile.attendance?.pct_present;
    if (p === null || p === undefined) return null;
    if (p >= 95) return { color: "emerald", text: t("attendance_excellent") };
    if (p >= 85) return { color: "blue", text: t("attendance_good") };
    if (p >= 70) return { color: "amber", text: t("attendance_warning") };
    return { color: "rose", text: t("attendance_critical") };
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/students")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 me-1 rtl:rotate-180" /> {t("back_to_students")}
        </Button>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md border border-border hover:bg-muted/50 transition text-sm" data-testid="upload-photo-btn">
            <Upload className="w-4 h-4" />
            {uploadingPhoto ? t("uploading") + "…" : (profile.photo_url ? t("replace_photo") : t("choose_photo"))}
            <input type="file" accept={PHOTO_ACCEPT} onChange={onPickPhoto}
                   disabled={uploadingPhoto} className="hidden" data-testid="photo-input" />
          </label>
          {profile.photo_url && (
            <Button variant="outline" size="sm" onClick={removePhoto} className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30">
              <Trash2 className="w-4 h-4 me-1" /> {t("remove_photo")}
            </Button>
          )}
          <Button onClick={openEdit} data-testid="edit-student-btn">
            <Pencil className="w-4 h-4 me-1" /> {t("edit_profile")}
          </Button>
        </div>
      </div>

      {/* Hero */}
      <Card className="p-6 border shadow-none" data-testid="student-hero">
        <div className="flex flex-wrap items-start gap-6">
          <div className="w-24 h-24 rounded-2xl bg-primary/10 text-primary text-3xl font-bold grid place-items-center shrink-0 overflow-hidden">
            {profile.photo_url
              ? <img src={resolveFileUrl(profile.photo_url)} alt={profile.full_name_ar || profile.full_name} className="w-full h-full object-cover" />
              : (initials || <GraduationCap className="w-10 h-10" />)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-semibold truncate">{profile.full_name_ar || profile.full_name}</h1>
              {profile.active === false
                ? <Badge variant="destructive">{t("inactive")}</Badge>
                : <Badge className="bg-emerald-500 hover:bg-emerald-500">{t("active")}</Badge>}
              {attBadge && (
                <Badge className={`bg-${attBadge.color}-500 hover:bg-${attBadge.color}-500`}>
                  {attBadge.text}
                </Badge>
              )}
            </div>
            <div className="text-muted-foreground mt-1">
              {profile.grade_detail?.name_ar || profile.grade_detail?.name || "—"}
              {profile.class_detail && <span className="mx-2">·</span>}
              {profile.class_detail?.name}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-muted-foreground">
              {profile.national_id && <span className="flex items-center gap-1.5 font-mono" dir="ltr"><BadgeCheck className="w-3.5 h-3.5" /> {profile.national_id}</span>}
              {profile.dob && <span className="flex items-center gap-1.5"><CalendarIcon className="w-3.5 h-3.5" /> {profile.dob}</span>}
              {profile.nationality && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {profile.nationality}</span>}
              {profile.gender && <span>{genderLabel(profile.gender)}</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full sm:w-auto sm:max-w-md">
            <Stat icon={ShieldCheck} value={`${profile.attendance?.pct_present ?? "—"}%`} label={t("attendance_rate")} />
            <Stat icon={Award} value={profile.behavior?.net ?? 0} label={t("behavior_score")} />
            <Stat icon={Wallet} value={`${(profile.wallet?.balance ?? 0).toFixed(2)}`} label={t("wallet_balance")} />
            <Stat icon={DollarSign} value={`${(profile.fees_due ?? 0).toFixed(0)}`} label={t("fees_due")} accent={profile.fees_due > 0 ? "rose" : null} />
          </div>
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview"><BadgeCheck className="w-4 h-4 me-1" /> {t("overview")}</TabsTrigger>
          <TabsTrigger value="family" data-testid="tab-family"><UsersIcon className="w-4 h-4 me-1" /> {t("family")}</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance"><ShieldCheck className="w-4 h-4 me-1" /> {t("attendance")}</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance"><DollarSign className="w-4 h-4 me-1" /> {t("finance")}</TabsTrigger>
          <TabsTrigger value="behavior" data-testid="tab-behavior"><Award className="w-4 h-4 me-1" /> {t("behavior")}</TabsTrigger>
          <TabsTrigger value="medical" data-testid="tab-medical"><Heart className="w-4 h-4 me-1" /> {t("medical")}</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4 grid md:grid-cols-2 gap-4">
          <Card className="p-4 border border-border/60 rounded-2xl shadow-sm md:col-span-2" data-testid="student-login-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center"><KeyRound className="w-5 h-5" /></div>
                <div>
                  <div className="text-sm font-semibold">{t("student_login_code")}</div>
                  <div className="text-xs text-muted-foreground">{t("student_login_code_hint")}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xl font-bold tracking-[0.25em] px-3 py-1.5 rounded-lg bg-muted" dir="ltr" data-testid="student-login-code-value">
                  {profile.login_code || "— — —"}
                </span>
                <Button size="sm" variant="outline" onClick={regenLoginCode} data-testid="regen-login-code-btn">
                  {profile.login_code ? t("regenerate") : t("generate")}
                </Button>
              </div>
            </div>
          </Card>
          <InfoCard title={t("personal_info")} icon={BadgeCheck} rows={[
            [t("name_en"), profile.full_name],
            [t("national_id"), profile.national_id],
            [t("date_of_birth"), profile.dob],
            [t("gender"), profile.gender && genderLabel(profile.gender)],
            [t("nationality"), profile.nationality],
            [t("blood_type"), profile.blood_type],
            [t("address"), profile.address],
          ]} t={t} />
          <InfoCard title={t("enrollment_info")} icon={GraduationCap} rows={[
            [t("enrollment_number"), profile.enrollment_number],
            [t("enrollment_date"), profile.enrollment_date],
            [t("grade"), profile.grade_detail?.name_ar || profile.grade_detail?.name],
            [t("class"), profile.class_detail?.name],
            [t("class_homework_count"), profile.class_homework_count || 0],
          ]} t={t} />
          {profile.bio && (
            <Card className="p-5 border shadow-none md:col-span-2">
              <h3 className="font-semibold mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /> {t("bio")}</h3>
              <p className="text-sm whitespace-pre-wrap leading-relaxed text-muted-foreground">{profile.bio}</p>
            </Card>
          )}
        </TabsContent>

        {/* FAMILY */}
        <TabsContent value="family" className="mt-4 space-y-4">
          <Card className="p-5 border shadow-none">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2"><UsersIcon className="w-4 h-4 text-primary" /> {t("guardians")}</h3>
              <Button size="sm" onClick={() => setGuardOpen(true)} data-testid="add-guardian-btn">
                <Plus className="w-4 h-4 me-1" /> {t("add")}
              </Button>
            </div>
            {(profile.guardians || []).length === 0
              ? <EmptyHint text={t("no_guardians_added")} />
              : (
                <div className="space-y-2">
                  {profile.guardians.map((g, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/30">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{g.name}</span>
                          {g.is_primary && <Badge className="bg-amber-500 hover:bg-amber-500 text-xs">{t("primary_guardian")}</Badge>}
                          {g.user_id && <Badge variant="outline" className="text-xs border-emerald-400 text-emerald-700">مرتبط بحساب</Badge>}
                          {!g.can_pickup && <Badge variant="destructive" className="text-xs">{t("cannot_pickup")}</Badge>}
                        </div>
                        <div className="text-sm text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                          {g.relation && <span>{g.relation}</span>}
                          {g.phone && <span className="font-mono" dir="ltr"><Phone className="w-3 h-3 inline me-1" />{g.phone}</span>}
                          {g.email && <span><Mail className="w-3 h-3 inline me-1" />{g.email}</span>}
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeGuardian(idx)} data-testid={`remove-guardian-${idx}`}>
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
          </Card>

          {/* Parent login account */}
          <Card className="p-5 border shadow-none" data-testid="parent-account-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> {t("parent_login_account")}</h3>
            </div>
            {profile.parent_account ? (
              <div className="space-y-2">
                <Row label={t("email")} value={profile.parent_account.email} mono />
                <Row label={t("name")} value={profile.parent_account.name} />
                <Row label={t("phone")} value={profile.parent_account.phone} mono />
                <Row label={t("status")} value={
                  profile.parent_account.active
                    ? <Badge className="bg-emerald-500 hover:bg-emerald-500">{t("active")}</Badge>
                    : <Badge variant="destructive">{t("inactive")}</Badge>
                } />
                <div className="pt-2">
                  <Button variant="outline" onClick={createParentAccount} data-testid="reset-parent-pwd-btn">
                    <KeyRound className="w-4 h-4 me-1" /> {t("reset_password")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 space-y-3">
                <KeyRound className="w-10 h-10 text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">{t("no_parent_account_yet")}</p>
                {(profile.guardians || []).some((g) => g.email) ? (
                  <Button onClick={createParentAccount} data-testid="create-parent-account-btn">
                    <KeyRound className="w-4 h-4 me-1" /> {t("create_parent_account")}
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground">{t("add_guardian_email_first")}</p>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ATTENDANCE */}
        <TabsContent value="attendance" className="mt-4">
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary" /> {t("attendance_last_60_days")}
            </h3>
            {(profile.attendance?.days_total ?? 0) === 0 ? (
              <EmptyHint text={t("no_attendance_data")} />
            ) : (
              <div className="grid sm:grid-cols-3 gap-4">
                <BigStat label={t("days_recorded")} value={profile.attendance.days_total} />
                <BigStat label={t("days_present")} value={profile.attendance.days_present} accent="emerald" />
                <BigStat label={t("rate")} value={`${profile.attendance.pct_present}%`} accent={profile.attendance.pct_present >= 85 ? "emerald" : "rose"} />
              </div>
            )}
          </Card>
        </TabsContent>

        {/* FINANCE */}
        <TabsContent value="finance" className="mt-4 grid md:grid-cols-2 gap-4">
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Wallet className="w-4 h-4 text-primary" /> {t("canteen_wallet")}</h3>
            {profile.wallet ? (
              <div className="space-y-2">
                <BigStat label={t("balance")} value={`${(profile.wallet.balance ?? 0).toFixed(2)} ${t("sar_unit")}`}
                         accent={profile.wallet.balance > 0 ? "emerald" : null} />
                <div className="text-xs text-muted-foreground">
                  {t("daily_limit")}: {profile.wallet.daily_limit ?? "—"}
                </div>
              </div>
            ) : (
              <EmptyHint text={t("no_wallet_yet")} />
            )}
          </Card>
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-primary" /> {t("school_fees")}</h3>
            <BigStat label={t("amount_due")} value={`${(profile.fees_due ?? 0).toFixed(2)} ${t("sar_unit")}`}
                     accent={profile.fees_due > 0 ? "rose" : "emerald"} />
            <p className="text-xs text-muted-foreground mt-2">
              {profile.fees_due > 0 ? t("has_outstanding_fees") : t("no_outstanding_fees")}
            </p>
          </Card>
        </TabsContent>

        {/* BEHAVIOR */}
        <TabsContent value="behavior" className="mt-4">
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-primary" /> {t("behavior_summary")}</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <BigStat label={t("positive_points")} value={profile.behavior.positive_points} accent="emerald" />
              <BigStat label={t("negative_points")} value={profile.behavior.negative_points} accent="rose" />
              <BigStat label={t("net_score")} value={profile.behavior.net}
                       accent={profile.behavior.net >= 0 ? "emerald" : "rose"} />
            </div>
          </Card>
        </TabsContent>

        {/* MEDICAL */}
        <TabsContent value="medical" className="mt-4">
          <Card className="p-5 border shadow-none">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-primary" /> {t("medical_info")}</h3>
            <div className="space-y-3 text-sm">
              <Row label={t("blood_type")} value={profile.blood_type || t("not_specified")} />
              <Row label={t("medical_notes")} value={profile.medical_notes || t("none")} />
            </div>
            {!profile.medical_notes && (
              <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
                💡 {t("medical_notes_hint")}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("edit_student_profile")}</DialogTitle></DialogHeader>
          <EditStudentForm form={form} setForm={setForm} t={t} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>{t("cancel")}</Button>
            <Button onClick={saveEdit} data-testid="save-profile-btn">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guardian dialog */}
      <Dialog open={guardOpen} onOpenChange={setGuardOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("add_guardian")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("name")} *</Label><Input value={guardForm.name} onChange={(e) => setGuardForm({ ...guardForm, name: e.target.value })} data-testid="g-name" /></div>
            <div><Label>{t("relation")}</Label><Input value={guardForm.relation} onChange={(e) => setGuardForm({ ...guardForm, relation: e.target.value })} placeholder="أب / أم / عم" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("phone")}</Label><Input value={guardForm.phone} onChange={(e) => setGuardForm({ ...guardForm, phone: e.target.value })} dir="ltr" /></div>
              <div><Label>{t("email")}</Label><Input type="email" value={guardForm.email} onChange={(e) => setGuardForm({ ...guardForm, email: e.target.value })} dir="ltr" /></div>
            </div>
            <div className="flex flex-wrap gap-4 pt-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={guardForm.is_primary} onChange={(e) => setGuardForm({ ...guardForm, is_primary: e.target.checked })} className="w-4 h-4" data-testid="g-primary" />
                {t("primary_guardian")}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={guardForm.can_pickup} onChange={(e) => setGuardForm({ ...guardForm, can_pickup: e.target.checked })} className="w-4 h-4" />
                {t("allowed_to_pickup")}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGuardOpen(false)}>{t("cancel")}</Button>
            <Button onClick={addGuardian} data-testid="guardian-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials dialog */}
      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" />
              {creds?.action === "created" ? t("account_created") : t("password_reset")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200">
              <AlertCircle className="w-4 h-4 inline me-1" />
              {creds?.reset_email_sent
                ? `تم إرسال رابط تعيين كلمة المرور إلى ${creds?.email} (صالح 24 ساعة).`
                : `تعذّر إرسال البريد إلى ${creds?.email}. تحقق من إعدادات البريد الإلكتروني.`}
            </div>
            <div className="space-y-2">
              <Label>{t("email")}</Label>
              <div className="flex gap-2">
                <Input value={creds?.email || ""} readOnly className="font-mono" />
                <Button size="icon" variant="outline" onClick={() => copyToClipboard(creds?.email)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredOpen(false)}>{t("close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, value, label, accent }) {
  const tone = accent === "rose"
    ? "text-rose-600"
    : accent === "emerald"
    ? "text-emerald-600"
    : "text-foreground";
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 border border-border">
      <Icon className="w-5 h-5 text-primary shrink-0" />
      <div>
        <div className={`text-xl font-bold leading-none ${tone}`}>{value}</div>
        <div className="text-[11px] text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

function BigStat({ label, value, accent }) {
  const tone = accent === "rose"
    ? "text-rose-600"
    : accent === "emerald"
    ? "text-emerald-600"
    : "text-foreground";
  return (
    <div className="p-4 rounded-xl border border-border bg-muted/30">
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className={`text-3xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

function InfoCard({ title, icon: Icon, rows, t }) {
  const visible = rows.filter(([_, v]) => v !== null && v !== undefined && v !== "");
  return (
    <Card className="p-5 border shadow-none">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-primary" />
        <h3 className="font-semibold">{title}</h3>
      </div>
      {visible.length === 0 ? (
        <EmptyHint text={t("no_data_filled")} />
      ) : (
        <div className="divide-y divide-border text-sm">
          {visible.map(([k, v]) => (
            <div key={k} className="flex justify-between py-2 gap-3">
              <span className="text-muted-foreground">{k}</span>
              <span className="font-medium text-end">{v}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between text-sm gap-3 py-1.5 border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono" : ""}>{value || "—"}</span>
    </div>
  );
}

function EmptyHint({ text }) {
  return <div className="text-center text-sm text-muted-foreground py-6 border border-dashed border-border rounded-lg">{text}</div>;
}

function EditStudentForm({ form, setForm, t }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <Tabs defaultValue="basic" className="mt-2">
      <TabsList>
        <TabsTrigger value="basic">{t("basic_info")}</TabsTrigger>
        <TabsTrigger value="enrollment">{t("enrollment_info")}</TabsTrigger>
        <TabsTrigger value="medical">{t("medical")}</TabsTrigger>
        <TabsTrigger value="bio">{t("bio")}</TabsTrigger>
      </TabsList>

      <TabsContent value="basic" className="mt-4 grid sm:grid-cols-2 gap-3">
        <div><Label>{t("name_ar")} *</Label><Input value={form.full_name_ar || ""} onChange={(e) => set("full_name_ar", e.target.value)} data-testid="ef-name-ar" /></div>
        <div><Label>{t("name_en")}</Label><Input value={form.full_name || ""} onChange={(e) => set("full_name", e.target.value)} dir="ltr" /></div>
        <div><Label>{t("national_id")}</Label><Input value={form.national_id || ""} onChange={(e) => set("national_id", e.target.value)} dir="ltr" /></div>
        <div><Label>{t("date_of_birth")}</Label><Input type="date" value={form.dob || ""} onChange={(e) => set("dob", e.target.value)} /></div>
        <div>
          <Label>{t("gender")}</Label>
          <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">{t("gender_male")}</SelectItem>
              <SelectItem value="female">{t("gender_female")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>{t("nationality")}</Label><Input value={form.nationality || ""} onChange={(e) => set("nationality", e.target.value)} /></div>
        <div><Label>{t("phone")}</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} dir="ltr" /></div>
        <div><Label>{t("email")}</Label><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} dir="ltr" /></div>
        <div className="sm:col-span-2"><Label>{t("address")}</Label><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
      </TabsContent>

      <TabsContent value="enrollment" className="mt-4 grid sm:grid-cols-2 gap-3">
        <div><Label>{t("enrollment_number")}</Label><Input value={form.enrollment_number || ""} onChange={(e) => set("enrollment_number", e.target.value)} dir="ltr" /></div>
        <div><Label>{t("enrollment_date")}</Label><Input type="date" value={form.enrollment_date || ""} onChange={(e) => set("enrollment_date", e.target.value)} /></div>
        <div>
          <Label>{t("status")}</Label>
          <Select value={form.active === false ? "false" : "true"} onValueChange={(v) => set("active", v === "true")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">{t("active")}</SelectItem>
              <SelectItem value="false">{t("inactive")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </TabsContent>

      <TabsContent value="medical" className="mt-4 space-y-3">
        <div>
          <Label>{t("blood_type")}</Label>
          <Select value={form.blood_type || ""} onValueChange={(v) => set("blood_type", v)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map((bt) =>
                <SelectItem key={bt} value={bt}>{bt}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("medical_notes")}</Label>
          <Textarea
            value={form.medical_notes || ""}
            onChange={(e) => set("medical_notes", e.target.value)}
            rows={5}
            placeholder={t("medical_notes_placeholder")}
          />
        </div>
      </TabsContent>

      <TabsContent value="bio" className="mt-4">
        <Label>{t("bio")}</Label>
        <Textarea value={form.bio || ""} onChange={(e) => set("bio", e.target.value)} rows={6} />
      </TabsContent>
    </Tabs>
  );
}
